//================================================================
//  Component: Raise PO Tab
//================================================================

//  Purpose: This contains the raise PO information for a commercial task

//  Properties:
//    - task = {An object, contains the commercial task details}

//  Example:
//    <RaisePOTab
//      task={task}
//    ></RaisePOTab>    

// ==============================================================

// Libraries
import React, { useReducer, useState, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom';

// Contexts
import { GetUser } from '../../../../Library/GlobalContexts';
import { SetToast } from '../../../../Library/GlobalContexts';

// Components
import Tooltip from '../../../../Components/Tooltip/Tooltip';
import LiveField from '../../../../Components/LiveField/LiveField';
import AddPOLineItemPane from './AddPOLineItemPane';
import KebabDropdown from '../../../../Components/KebabDropdown/KebabDropdown';
import EditPOLineItemPane from './EditPOLineItemPane';

// Functions
import WriteDocument from '../../../../Library/WriteDocument';
import DocumentListener from '../../../../Library/DocumentListener';
import ConvertDate from '../../../../Library/ConvertDate';
import AddRemoveDocumentArray from '../../../../Library/AddRemoveDocumentArray';
import CreateRequest from '../../../../Library/CreateRequest';

// Images
import Expand from '../../../../Components/Images/Icon_Collapse_Teal.svg';
import Collapse from '../../../../Components/Images/Icon_Expand_Teal.svg';
import Add from '../../../../Components/Images/Icon_Add_Teal.svg';
import AddDisabled from '../../../../Components/Images/Icon_Add_Grey.svg';
import Info from '../../../../Components/Images/Icon_Info_Blue.svg';
import ExternalLink from '../../../../Components/Images/Icon_ExternalLink_Blue.svg';
import Loading from '../../../../Components/Images/Image_Loading_Ripple.svg';

// CSS
import '../../Task.css'

export default function RaisePOTab({
    task
}) {

    //------------------------------------------------------
    //  useContexts and React Router
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);
    const navigate = useNavigate();

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to determine the pageStatus
    const [pageStatus, setPageStatus] = useState('onload');

    // Used to toggle the visibility of the add PO line item pane
    const [addPOLineItemPaneOpen, setAddPOLineItemPaneOpen] = useState(false);

    // Used to toggle the visibility of the edit PO line item pane
    const [editPOLineItemPaneOpen, setEditPOLineItemPaneOpen] = useState(false);

    // Used to store the selected PO Line Item
    const [selectedPOLineItemIndex, setSelectedPOLineItemIndex] = useState(null);

    // Used to toggle of the raise PO checkbox
    const [isRaisePOChecked, setIsRaisePOChecked] = useState(task?.poraised ?? true);

    // Used to store agreement task
    const [agreementTask, setAgreementTask] = useState(undefined);

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store whether a section is expanded
    const [taskSection, setTaskSection] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'poRequestCollapsed': true,
            'agreementInformationCollapsed': true,
            'financialInformationCollapsed': true,
        }
    );

    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------

    // Document Listener
    // Find the agreement task document
    useEffect(() => {

        const agreementId = task?.agreement?.agreementid;

        if (agreementId === '') return;
        if (agreementId === null) return;
        if (agreementId === undefined) return;


        function onLoadChange(document) {

            setAgreementTask(document);

        }

        function onError(error) {
            console.log('error', error)

        }

        const unsubscribe = DocumentListener('agreements', agreementId, onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        };

        // eslint-disable-next-line
    }, []);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Function used to complete the request/ticket
    function closeTicket() {

        setPageStatus('pending');

        const allPromises = [];

        //=================================================
        //  Update document in Firestore
        //=================================================

        const updatedDoc = {
            'stage': 'Completed',
            'status': 'Completed',
            'lastmodifiedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            },
            'lastmodifieddate': new Date(),
        };

        const updatedActivityFeed = {
            'activitydate': new Date(),
            'actionedby': {
                'email': getUser?.emailaddress,
                'givenname': getUser?.givenname,
                'surname': getUser?.surname
            },
            'action': 'completed',
            'comments': ''
        };

        //=================================================
        //  Check if Raise PO checkbox selected
        //=================================================

        if (task?.poraised === true) {

            return CreateRequest()
            .then((requestIds) => {

                const procurementTask = {
                    'requestid': requestIds[0],
                    'parentrequestid': task?.parentrequestid,
                    'tasktype': 'Procurement',
                    'taskname': 'Procurement Task',
                    'taskdescription': `New PO Request for ${task?.applicationname}`,
                    'applicationname': task?.applicationname  === '' || task?.applicationname === undefined ? '' : task?.applicationname,
                    'initiativename': task?.initiativename === '' || task?.initiativename === undefined ? '' : task?.initiativename,
                    'stage': '',
                    'status': 'Not Started',
                    'assignmentgroup': 'Procurement',
                    'requesttype': 'Procurement',
                    'activityfeedvisible': true,
                    'approvalhubvisible': false,
                    'region': task?.region,
                    'assignedto': {
                        'email': 'Unassigned',
                        'givenname': '',
                        'surname': ''
                    },
                    'assigneddate': null,
                    'createddate': new Date(),
                    'createdby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'lastmodifieddate': new Date(),
                    'lastmodifiedby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'activityfeed': [
                        {
                            'activitydate': new Date(),
                            'actionedby': {
                                'email': getUser?.emailaddress,
                                'givenname': getUser?.givenname,
                                'surname': getUser?.surname
                            },
                            'action': 'created',
                            'comments': '',
                        }
                    ],

                    'agreementid': task?.agreement?.agreementid,
                    'requestername': task?.requestername,
                    'shortdescription': task?.potask?.shortdescription,
                    'commercialscope': task?.potask?.commercialscope,
                    'sponsorname': task?.potask?.sponsorname === undefined ? task?.sponsorname : task?.potask?.sponsorname,
                    'poanumber': agreementTask?.poanumber,
                    'agreementtype': agreementTask?.agreementtype,
                    'vendor': {
                        'vendorname': agreementTask?.vendor?.vendorname === undefined ? '' : agreementTask?.vendor?.vendorname,
                        'vendorid': agreementTask?.vendor?.vendorid === undefined ? '' : agreementTask?.vendor?.vendorid,
                        'tradingas': agreementTask?.vendor?.tradingas === undefined ? '' : agreementTask?.vendor?.tradingas
                    },
                    'lendleasecompany': agreementTask?.lendleaseentity,
                    'parentcontract': agreementTask?.parentcontract,
                    'agreementstartdate': agreementTask?.agreementstartdate,
                    'agreementenddate': agreementTask?.agreementenddate,
                    'documentstoragelink': agreementTask?.documentstoragelink,

                    'currency': task?.potask?.currency === undefined ? task?.currency : task?.potask?.currency,
                    'estannualcontractvalue': task?.potask?.estannualcontractvalue === undefined ? task?.estannualcontractvalue : task?.potask?.estannualcontractvalue,
                    'esttotalcontractvalue': task?.potask?.esttotalcontractvalue === undefined ? task?.esttotalcontractvalue : task?.potask?.esttotalcontractvalue,
                    'ponumber': task?.potask?.ponumber === undefined ? '' : task?.potask?.ponumber,
                    'polineitems': task?.potask?.polineitems === undefined ? [] : task?.potask?.polineitems,

                    'digitalcommercialrequest': {
                        'parentrequestid': task?.parentrequestid,
                        'requesttype': task?.requesttype,
                        'requestorigin': task?.requestorigin,
                        'applicationname': task?.applicationname,
                        'shortdescription': task?.shortdescription,
                        'requestername': task?.requestername,
                        'region': task?.region,
                        'sponsorname': task?.sponsorname,
                        'createdby': task?.createdby,
                        'supportingdocs': task?.supportingdocs === undefined ? [] : task?.supportingdocs,
                        'commercialscope': task?.commercialscope,
                        'buysellagreement': task?.buysellagreement,
                        'poanumber': agreementTask?.poanumber,
                        'agreementtype': agreementTask?.agreementtype,
                        'agreementstartdate': agreementTask?.agreementstartdate,
                        'agreementenddate': agreementTask?.agreementenddate,
                        'lendleasecompany': agreementTask?.lendleaseentity,
                        'vendor': {
                            'vendorname': agreementTask?.vendor?.vendorname === undefined ? '' : agreementTask?.vendor?.vendorname,
                            'vendorid': agreementTask?.vendor?.vendorid === undefined ? '' : agreementTask?.vendor?.vendorid,
                            'tradingas': agreementTask?.vendor?.tradingas === undefined ? '' : agreementTask?.vendor?.tradingas
                        },
                        'currency': task?.currency,
                        'estannualcontractvalue': task?.estannualcontractvalue,
                        'esttotalcontractvalue': task?.esttotalcontractvalue,
                        'projectcode': task?.projectcode,
                        'tasknumber': task?.tasknumber,
                        'expendituretype': task?.expendituretype,
                        
                        // Digital Commercial Request Fields
                        'problemtosolve': task?.problemtosolve === undefined ? '' : task?.problemtosolve, 
                        'expectedoutcome': task?.expectedoutcome === undefined ? '' : task?.expectedoutcome,
                        'expectedimpact': task?.expectedimpact === undefined ? '' : task?.expectedimpact,
                        'impact': task?.impact === undefined ? '' : task?.impact,
                        'urgency': task?.urgency === undefined ? '' : task?.urgency,                  
                        'budgetowner': task?.budgetowner === undefined ? 
                            {
                                'email': '',
                                'givenname': '',
                                'surname': ''
                            } 
                            : task?.budgetowner,    
                        'financemanager': task?.financemanager === undefined ? 
                            {
                                'email': '',
                                'givenname': '',
                                'surname': ''
                            } 
                            : task?.financemanager, 
                    }
                }

                return WriteDocument('requests', requestIds[0], procurementTask, true)
                .then(() => {

                    //=================================================
                    // Close Commercial Task
                    //=================================================

                    allPromises.push(
                        WriteDocument('requests', task?.requestid, updatedDoc, true)
                    );

                    allPromises.push(
                        AddRemoveDocumentArray('requests', task?.requestid, 'activityfeed', updatedActivityFeed, 'add', updatedActivityFeed.actionedby)
                    );

                    return Promise.all(allPromises)
                    .then(() => {

                        // Go to Triage Tab and Scroll to Top
                        navigate(`/${window.location.pathname.split('/')[1]}/${task?.requestid}?view=triage`)
        
                        const body = document.querySelector('.PageComponent-Container');
        
                        body.scrollIntoView({
                            behavior: 'smooth'
                        }, 300);
        
                    })
                })
            })
            .catch((error) => {
                console.log('Error', error)
            });

        }

        //=================================================
        // Close Commercial Task
        //=================================================

        allPromises.push(
            WriteDocument('requests', task?.requestid, updatedDoc, true)
        );

        allPromises.push(
            AddRemoveDocumentArray('requests', task?.requestid, 'activityfeed', updatedActivityFeed, 'add', updatedActivityFeed.actionedby)
        );

        return Promise.all(allPromises)
        .then(() => {

            setPageStatus('onload');

            // Go to Triage Tab and Scroll to Top
            navigate(`/${window.location.pathname.split('/')[1]}/${task?.requestid}?view=triage`);

            const body = document.querySelector('.PageComponent-Container');

            body.scrollIntoView({
                behavior: 'smooth'
            }, 300);

        })
        .catch((error) => {
            
            console.log('Error', error)

            // NH TODO - WE NEED TO ADD AN ERROR STATE HERE
            
        });

    }

    // Function used to handle the raise PO checkbox
    function handleRaisePO(value) {

        setIsRaisePOChecked(value);

        // If value is True need to open PO Request Section, Contract Information and Financial Information
        if (value) {

            setTaskSection({
                'poRequestCollapsed': false,
                'agreementInformationCollapsed': false,
                'financialInformationCollapsed': false
            });

        } else {

            setTaskSection({
                'poRequestCollapsed': true,
                'agreementInformationCollapsed': true,
                'financialInformationCollapsed': true
            });

        }

        // Update the document
        const updatedDoc = {
            'poraised': value,
        };

        WriteDocument('requests', task?.requestid, updatedDoc, true)
            .catch((error) => {

                setToast({
                    'type': 'error',
                    'message': 'Failed to update PO Raise status',
                });

                // Set the value back to the original value
                setIsRaisePOChecked(!value);
                console.log('Error', error)

            });

    }

    // Handle edit button click and set the selected PO line item
    const handlePOItemEditClick = (polineitemIndex) => {

        // Set the selected PO Line Item Index
        setSelectedPOLineItemIndex(polineitemIndex);

        // Open the Edit PO Line Item Pane
        setEditPOLineItemPaneOpen(true);

    };

    // Handle delete button click and set the selected PO line item
    const handlePOItemDeleteClick = (polineitemIndex) => {

        const updatedPOLineItems = task?.potask?.polineitems.filter((item, index) => index !== polineitemIndex);

        // Update the document
        const updatedDoc = {
            'potask': {
                'polineitems': updatedPOLineItems
            }
        };

        WriteDocument('requests', task?.requestid, updatedDoc, true)
            .catch((error) => {

                setToast({
                    'type': 'error',
                    'message': 'Failed to delete PO Line Item',
                });

                console.log('Error', error)

            })
    };

    //------------------------------------------------------
    //  Constants
    //------------------------------------------------------

    // Validate if the sponsor email is missing or hasn't been updated
    const isSponsorEmailValid = task?.potask?.sponsorname?.email?.length > 0 ||
        (task?.potask?.sponsorname?.email === undefined && task?.sponsorname?.email?.length > 0);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    // =====================================================
    //  Pending 
    // =====================================================
    
    if (pageStatus === 'pending') {
        return (
            <div className='Task-Section-Container'>
                <div className='flex flex-col  items-center px-[15%] py-[5%]'>
                    <img alt='loading-circle-icon' src={Loading}></img>
                    <button className='Primary-Button' onClick={() => setPageStatus('onload')}>
                        Cancel
                    </button>
                </div>
            </div>
        )
    }

    // =====================================================
    //  Onload 
    // =====================================================

    else {
        return (
            <div className='flex flex-col gap-2 p-0'>
    
                {/* ===================================================== */}
                {/*  Raise PO Checkbox                                    */}
                {/* ===================================================== */}
    
                <div className='flex flex-row items-start gap-2 mb-2 mt-4'>
                    <label className='Checkbox-Container'>
                        <input
                            required
                            type='checkbox'
                            name='confirmation'
                            checked={isRaisePOChecked}
                            onChange={() => handleRaisePO(!isRaisePOChecked)}
                            disabled={task?.status === 'Completed' || task?.stage !== 'Raise PO'}
                        ></input>
                        <span className='Checkbox'></span>
                    </label>
    
                    <label className='max-w-[400px]'>
                        Raise PO
                    </label>
                </div>
    
                {/* ===================================================== */}
                {/*  PO Request Information                               */}
                {/* ===================================================== */}
    
                {
                    isRaisePOChecked &&
                    <div className='Task-Section-Container'>
    
                        <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'poRequestCollapsed': !taskSection.poRequestCollapsed })}>
                            <img
                                className='cursor-pointer'
                                src={taskSection?.poRequestCollapsed ? Collapse : Expand}
                                alt={'>'}
                            ></img>
                            <h4 className='text-[20px] m-0'>PO Request Information</h4>
                        </div>
                        <hr className='m-0' hidden={taskSection?.poRequestCollapsed}></hr>
    
                        {/* ------------- PO REQUEST INFORMATION ------------- */}
    
                        <div className='p-[3%] pt-1' hidden={taskSection?.poRequestCollapsed}>
    
                            {/* =========================================================== */}
                            {/*  Request Type                                               */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>Request Type</label>
                                <input
                                    className='Input-Field-Text'
                                    style={{ width: '400px' }}
                                    type='text'
                                    value={task?.requesttype}
                                    disabled
                                ></input>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Requester Name                                             */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>Name of Requester</label>
                                <input
                                    className='Input-Field-Text'
                                    style={{ width: '400px' }}
                                    type='text'
                                    value={task?.requestername}
                                    disabled
                                ></input>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Short Description of Request                               */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    styleInput={{ width: '100%' }}
                                    type={'text'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.shortdescription'}
                                    label={<label className='font-medium'> Short Description of Request <span className='text-[#C4314B]'>*</span></label>}
                                    defaultValue={`Raise PO for ${task?.applicationname}`}
                                    placeholder={'Enter a short description of the request'}
                                    disabled={task?.status === 'Completed'}
                                ></LiveField>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Detailed Request / Scope Description                       */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    styleInput={{ width: '100%' }}
                                    type={'multiline'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.commercialscope'}
                                    label={<label className='font-medium'> Detailed Request / Scope Description <span className='text-[#C4314B]'>*</span></label>}
                                    defaultValue={`Raise PO for ${task?.applicationname}`}
                                    placeholder={'Enter commercial scope of this request'}
                                    disabled={task?.status === 'Completed'}
                                ></LiveField>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Sponsor Name                                               */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'user-search'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.sponsorname'}
                                    label={<label className='font-medium'> Sponsor Name <span className='text-[#C4314B]'>*</span></label>}
                                    defaultValue={task?.potask?.sponsorname === undefined ? task?.sponsorname : task?.potask?.sponsorname}
                                    disabled={(task?.status === 'Completed' || task?.status === 'Rejected') ? true : false}
                                ></LiveField>
                            </div>
                        </div>
    
                    </div>
                }
    
                {/* ===================================================== */}
                {/*  Agreement Information                                */}
                {/* ===================================================== */}
    
                {
                    isRaisePOChecked &&
                    <div className='Task-Section-Container'>
    
                        <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'agreementInformationCollapsed': !taskSection.agreementInformationCollapsed })}>
                            <img
                                className='cursor-pointer'
                                src={taskSection?.agreementInformationCollapsed ? Collapse : Expand}
                                alt={'>'}
                            ></img>
                            <h4 className='text-[20px] m-0'>Agreement Information</h4>
                        </div>
                        <hr className='m-0' hidden={taskSection?.agreementInformationCollapsed}></hr>
    
                        {/* ------------- AGREEMENT INFORMATION ------------- */}
    
                        <div className='p-[3%] pt-1' hidden={taskSection?.agreementInformationCollapsed}>
    
                            {/* Info Label */}
                            <div className='flex flex-row gap-3 px-[15px] py-[12px] text-[15px] rounded-md mx-1 mb-1 mt-4 bg-[#E9F7FF] border-1 border-[#0972D3]' hidden={agreementTask === undefined}>
                                <img src={Info} alt='info'></img>
                                <p className='mb-0 leading-6'>
                                    <b className='font-medium'>{agreementTask?.createdby?.givenname} {agreementTask?.createdby?.surname}</b> has added this agreement into Inventory on {ConvertDate(agreementTask?.createddate)}.
                                    <br></br>
                                    <a 
                                        className='flex flex-row gap-2 items-center ' 
                                        href={
                                            process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? `https://devinventory.digitalservices.lendlease.com/inventory/agreement/${agreementTask?.agreementid}?view=overview` 
                                            : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? `https://uatinventory.digitalservices.lendlease.com/inventory/agreement/${agreementTask?.agreementid}?view=overview` 
                                            : `https://inventory.digitalservices.lendlease.com/inventory/agreement/${agreementTask?.agreementid}?view=overview`
                                        } 
                                        target='_blank' rel='noopener noreferrer'
                                    >
                                        Make changes to agreement in Inventory
                                        <img src={ExternalLink} alt='link'></img>
                                    </a>
                                </p>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  PoA Number                                                 */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <label className='font-medium'> PoA Number </label>
                                <input
                                    className='Input-Field-Text'
                                    style={{ width: '200px' }}
                                    type='text'
                                    value={agreementTask?.poanumber}
                                    disabled
                                ></input>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Agreement Type                                             */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'choice'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.agreementtype'}
                                    defaultValue={agreementTask?.agreementtype}
                                    disabled={true}
                                    label={<label className='font-medium'> Agreement Type <span className='text-[#C4314B]'>*</span></label>}
                                    options={[
                                        'Consultancy Services Agreement (CSA)',
                                        'Hardware Order',
                                        'Letter of Intent (LoI)',
                                        'Master Services Agreement (MSA)',
                                        'Non-Disclosure Agreement (NDA)',
                                        'Novation/Assignment Agreement',
                                        'Service Order (SO)',
                                        'Software License',
                                        'Subscription',
                                        'Supply Agreement',
                                        'Variation'
                                    ]}
                                    styleInput={{ width: '400px' }}
                                ></LiveField>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Vendor                                                     */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'lookup'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.vendor'}
                                    label={<label className='font-medium'> Vendor <span className='text-[#C4314B]'>*</span></label>}
                                    defaultValue={agreementTask?.vendor?.vendorname}
                                    placeholder={'Search vendor by name'}
                                    dataFieldCollectionid={'vendors'}
                                    dataFieldPrimary={'vendorname'}
                                    dataFieldSecondary={'tradingas'}
                                    dataFieldTertiary={'vendorid'}
                                    disabled={true}
                                    styleInput={{ width: '400px' }}
                                ></LiveField>
    
                                <div className='w-[450px] flex flex-row gap-2 px-[15px] py-[7px] text-[15px] rounded-md mx-1 my-1 bg-[#E9F7FF] border-1 border-[#0972D3]'>
                                    <img src={Info} alt='info'></img>
                                    <p className='mb-0'>
                                        To add a new vendor, click <a href={process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285' ? 'https://devinventory.digitalservices.lendlease.com/inventory?view=vendors' : process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396' ? 'https://uatinventory.digitalservices.lendlease.com/inventory?view=vendors' : 'https://inventory.digitalservices.lendlease.com/inventory?view=vendors'} target='_blank' rel='noopener noreferrer'>here</a>.
                                    </p>
                                </div>
    
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Lendlease Entity                                           */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
    
                                <LiveField
                                    type={'choice'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.lendleaseentity'}
                                    defaultValue={task?.agreement?.lendleaseentity}
                                    label={<label className='font-medium'> Lendlease Entity <span className='text-[#C4314B]'>*</span></label>}
                                    styleInput={{ width: '450px' }}
                                    disabled={true}
                                    options={[
                                        'TBC',
                                        'Lendlease Asia Holdings Pte. Ltd',
                                        'Lendlease Building Pty Ltd',
                                        'Lendlease Chilled Water (Barangaroo South) Pty Limited',
                                        'Lendlease Construction Pty Limited',
                                        'Lendlease Design Build',
                                        'Lendlease Development Pty Ltd',
                                        'Lendlease Digital Asia Pte Ltd',
                                        'Lendlease Digital Australia Holdings Pty Limited',
                                        'Lendlease Digital Australia Pty Ltd',
                                        'Lendlease Digital Investments Pty Limited',
                                        'Lendlease Digital US Holdings LLC',
                                        'Lendlease Digital US LLC',
                                        'Lendlease Embedded Network (Barangaroo South) Pty Limited',
                                        'Lendlease Engineering Pty Ltd',
                                        'Lendlease Europe Limited',
                                        'Lendlease Europe Limited',
                                        'Lendlease Global Commercial Trust Management Pte Ltd',
                                        'Lendlease Group Services Pty Limited',
                                        'Lendlease ICT Services, Inc',
                                        'Lendlease Management Services Limited',
                                        'Lendlease Project Management & Construction (Shanghai) Co Ltd',
                                        'Lendlease Recycled Water (Barangaroo South) Pty Limited',
                                        'Lendlease Retirement Living Holding Pty Limited',
                                        'Lendlease Services Pty Limited',
                                        'Lendlease Singapore Pte Ltd',
                                        'Lendlease Technology Delivery Pty Limited',
                                        'Lendlease Technology Pty Ltd',
                                        'Podium Asset Holdings Pty Limited',
                                        'Podium Property Services LLC',
                                        'Lendlease (Elephant & Castle) Ltd',
                                        'Lendlease (US) Construction Inc',
                                        'Lendlease (US) Construction, LMB Inc',
                                        'Lendlease (US) Development',
                                        'Lendlease (US) Partnerships',
                                        'Lendlease (US) Services Inc',
                                    ]}
                                ></LiveField>
    
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Parent Contract                                            */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'text'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.parentcontract'}
                                    defaultValue={agreementTask?.parentcontract?.poanumber === '' || agreementTask?.parentcontract?.poanumber === undefined ? '-' : agreementTask?.parentcontract?.poanumber}
                                    label={<label className='font-medium'> Parent Contract </label>}
                                    styleInput={{ width: '400px' }}
                                    disabled={true}
                                ></LiveField>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Agreement Start Date                                       */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'date-string'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.contractstartdate'}
                                    defaultValue={agreementTask?.agreementstartdate}
                                    label={<label className='font-medium'> Agreement Start Date <span className='text-[#C4314B]'>*</span></label>}
                                    styleInput={{ width: '400px' }}
                                    disabled={true}
                                    validation={{
                                        type: 'date',
                                        maxDate: task?.potask?.contractenddate
                                    }}
                                ></LiveField>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Agreement End Date                                         */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <label className='font-medium'>Agreement End Date <span className='text-[#C4314B]'>*</span></label>

                                {/* Checkbox to disable the date */}
                                <div className='flex flex-row items-start px-[5px]'>
                                    <label className='Checkbox-Container' style={{ cursor: 'not-allowed' }}>
                                        <input
                                            required
                                            type='checkbox'
                                            name='confirmation'
                                            checked={agreementTask?.agreementenddate === null}
                                            disabled={true}
                                        ></input>
                                        <span className='Checkbox'></span>
                                    </label>

                                    <label className='max-w-[400px]'>
                                        Agreement has no end date
                                    </label>
                                </div>

                                {
                                    agreementTask?.agreementenddate !== null && 
                                    <LiveField
                                        type={'date-string'}
                                        collectionid={'requests'}
                                        documentid={task?.requestid}
                                        fieldpath={'potask.contractenddate'}
                                        defaultValue={agreementTask?.agreementenddate}
                                        styleInput={{ width: '400px' }}
                                        disabled={true}
                                        validation={{
                                            type: 'date',
                                            minDate: task?.potask?.contractstartdate === undefined ? task?.contractstartdate : task?.potask?.contractstartdate
                                        }}
                                    ></LiveField>
                                }
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Document Storage Link                                      */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    styleInput={{ width: '100%' }}
                                    type={'text'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.documentstoragelink'}
                                    label={<label className='font-medium'> Document Storage Link <span className='text-[#C4314B]'>*</span></label>}
                                    defaultValue={agreementTask?.documentstoragelink}
                                    placeholder={'https://'}
                                    disabled={true}
                                    validation={{
                                        type: 'link'
                                    }}
                                ></LiveField>
                            </div>
    
                        </div>
    
                    </div>
                }
    
                {/* ===================================================== */}
                {/*  Financial Information                                */}
                {/* ===================================================== */}
    
                {
                    isRaisePOChecked &&
                    <div className='Task-Section-Container'>
    
                        <div className='flex flex-row gap-3 px-[2%] py-[18px] cursor-pointer' onClick={() => setTaskSection({ 'financialInformationCollapsed': !taskSection.financialInformationCollapsed })}>
                            <img
                                className='cursor-pointer'
                                src={taskSection?.financialInformationCollapsed ? Collapse : Expand}
                                alt={'>'}
                            ></img>
                            <h4 className='text-[20px] m-0'>Financial Information</h4>
                        </div>
                        <hr className='m-0' hidden={taskSection?.financialInformationCollapsed}></hr>
    
                        {/* ------------- FINANCIAL INFORMATION ------------- */}
    
                        <div className='p-[3%] pt-1' hidden={taskSection?.financialInformationCollapsed}>
    
                            {/* =========================================================== */}
                            {/*  Currency                                                   */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'choice'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.currency'}
                                    defaultValue={task?.potask?.currency === undefined ? task?.currency : task?.potask?.currency}
                                    label={<label className='font-medium'> Currency </label>}
                                    options={[
                                        'AUD',
                                        'SGD',
                                        'GBP',
                                        'USD',
                                        'EUR',
                                        'MYR'
                                    ]}
                                    styleInput={{ width: '400px' }}
                                    disabled={task?.status === 'Completed'}
                                ></LiveField>
                            </div>
    
                            {/* =========================================================== */}
                            {/*  Estimated Annual Contract Value (exTax)                    */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'text'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    placeholder={'Enter Value'}
                                    fieldpath={'potask.estannualcontractvalue'}
                                    defaultValue={task?.potask?.estannualcontractvalue === undefined ? task?.estannualcontractvalue : task?.potask?.estannualcontractvalue}
                                    label={<label className='font-medium'> Estimated Annual Contract Value (exTax) </label>}
                                    styleInput={{ width: '400px' }}
                                    disabled={task?.status === 'Completed'}
                                ></LiveField>
                            </div>
    
                            {/* =========================================================== */}
                            {/* Estimated Total Contract Value (exTax)                      */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'text'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.esttotalcontractvalue'}
                                    placeholder={'Enter Value'}
                                    defaultValue={task?.potask?.esttotalcontractvalue === undefined ? task?.esttotalcontractvalue : task?.potask?.esttotalcontractvalue}
                                    label={<label className='font-medium'> Estimated Total Contract Value (exTax) </label>}
                                    styleInput={{ width: '400px' }}
                                    disabled={task?.status === 'Completed'}
                                ></LiveField>
                            </div>
    
                            {/* =========================================================== */}
                            {/* PO Number (eFinance)                                        */}
                            {/* =========================================================== */}
    
                            <div className='FormComponent-Row'>
                                <LiveField
                                    type={'text'}
                                    collectionid={'requests'}
                                    documentid={task?.requestid}
                                    fieldpath={'potask.ponumber'}
                                    defaultValue={task?.potask?.ponumber}
                                    placeholder={'Enter PO Number'}
                                    label={<label className='font-medium'> PO Number (eFinance) </label>}
                                    styleInput={{ width: '400px' }}
                                    disabled={task?.status === 'Completed'}
                                ></LiveField>
                            </div>
                            <div className='FormComponent-Row'>
                                <label className='font-medium'> Setup PO Line Items </label>
    
                                {/*  PO Line Table */}
    
                                <div className='border border-[#D8D8D8] rounded'>
                                    <table className='table-fixed w-full max-h-96 overflow-y-scroll'>
                                        <thead>
                                            <tr className='border-b-[#D8D8D8] border-solid border-b-2'>
                                                <th className='py-[15px] px-[20px] font-medium text-sm'>Project Code</th>
                                                <th className='py-[15px] px-[8px] font-medium text-sm'>Task Number</th>
                                                <th className='py-[15px] px-[8px] font-medium text-sm'>Expenditure Type</th>
                                                <th className='py-[15px] px-[20px] font-medium text-sm'>Currency</th>
                                                <th className='py-[15px] px-[20px] font-medium text-sm'>PO Amount (exTax)</th>
                                                <th className='py-[15px] px-[20px] font-medium text-sm'>Tax rate (%)</th>
                                                <th className='py-[15px] px-[8px] font-medium text-sm'>Fiscal Year</th>
                                                <th className='py-[15px] px-[8px] font-medium text-sm'>Description</th>
    
                                                <th className='py-[15px] px-[15px] font-medium text-sm min-w-20'>
                                                    <Tooltip
                                                        message={'+ Add PO item'}
                                                        children={
                                                            <img className='cursor-pointer' src={task?.status === 'Completed' || task?.stage !== 'Raise PO' ? AddDisabled : Add} alt='add' onClick={() => task?.status === 'Completed' || task?.stage !== 'Raise PO' ? null : setAddPOLineItemPaneOpen(true)}></img>
                                                        }
                                                    ></Tooltip>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
    
                                                // No PO Line Items Found
                                                task?.potask?.polineitems?.length === 0 || task?.potask?.polineitems === undefined
                                                    ?
                                                    (
                                                        <tr>
                                                            <td colSpan={9}>
                                                                <div className='text-center p-[2%]'>
                                                                    Click on the '+' icon to add a new PO Line Item.
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                    :
                                                    (
    
                                                        // Map in each PO Line Item
                                                        task?.potask?.polineitems?.map((poLine, index) => (
                                                            <tr
                                                                key={index}
                                                                className='border-b-[#C8C8C9] border-solid border-b last:border-0 hover:bg-[#F0F7F7]'
                                                            >
    
                                                                {/* Project Code */}
                                                                <td className='py-[15px] px-[20px] text-sm'>
                                                                    {poLine?.projectcode}
                                                                </td>
    
                                                                {/* Task Number */}
                                                                <td className='py-[15px] px-[8px] text-sm'>
                                                                    {poLine?.tasknumber === '' || poLine?.tasknumber === undefined ? '-' : poLine?.tasknumber}
                                                                </td>
    
                                                                {/* Expenditure Type */}
                                                                <td className='py-[15px] px-[8px] text-sm'>
                                                                    {poLine?.expendituretype === '' || poLine?.expendituretype === undefined ? '-' : poLine?.expendituretype}
                                                                </td>
    
                                                                {/* Currency */}
                                                                <td className='py-[15px] px-[20px] text-sm'>{poLine?.currency}</td>
    
                                                                {/* PO Amount */}
                                                                <td className='py-[15px] px-[20px] text-sm'>{poLine?.poamount}</td>
    
                                                                {/* Tax Rate */}
                                                                <td className='py-[15px] px-[20px] text-sm'>{poLine?.taxrate}</td>
    
                                                                {/* Fiscal Year */}
                                                                <td className='py-[15px] px-[8px] text-sm'>{poLine?.fiscalyear}</td>
    
                                                                {/* Description */}
                                                                <td className='py-[15px] px-[8px] text-sm'>{poLine?.description === '' ? '-' : poLine?.description}</td>
    
                                                                {/* Dropdown with Edit and Remove options */}
                                                                {
                                                                    task?.stage !== 'Raise PO' || task?.status === 'Completed' ?
                                                                        <td className='py-[15px] px-[8px] text-sm'></td>
                                                                        :
                                                                        <td className='py-[15px] px-[15px]'>
                                                                            <KebabDropdown>
                                                                                <div className=' bg-white  rounded shadow-lg z-10'>
                                                                                    <button
                                                                                        onClick={() => handlePOItemEditClick(index)}
                                                                                        className='block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100'
                                                                                    >
                                                                                        Edit
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handlePOItemDeleteClick(index)}
                                                                                        className='block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100'
                                                                                    >
                                                                                        Remove
                                                                                    </button>
                                                                                </div>
                                                                            </KebabDropdown>
                                                                        </td>
                                                                }
                                                            </tr>
                                                        ))
                                                    )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                }
    
                {/* ===================================================== */}
                {/*  Move to Next Stage Button                            */}
                {/* ===================================================== */}
    
                <div className='flex flex-col gap-1 mt-4 mb-0'>
    
                    <button
                        className='Primary-Button w-fit'
                        onClick={() => closeTicket()}
                        disabled={
                            task?.stage !== 'Raise PO' ||
                            (
                                isRaisePOChecked && (
                                    !task?.potask?.shortdescription?.trim() ||         // Check if short description is empty or only whitespace
                                    !task?.potask?.commercialscope?.length ||          // Check if commercial scope is empty
                                    !isSponsorEmailValid                               // Check if sponsor name is empty
                                )
                            )
                        }
                    >
                        Complete
                    </button>
    
                    <label className='text-[14px] text-slate-600 italic my-2'>Clicking this button will close this request and update the status to Completed. If “Raise PO” has been selected, a PO request with the above information will be sent to the procurement team.</label>
    
                </div>
    
                {/* ===================================================== */}
                {/*  Add PO Line Item Pane                                */}
                {/* ===================================================== */}
    
                <AddPOLineItemPane
                    task={task}
                    addPOLineItemPaneOpen={addPOLineItemPaneOpen}
                    setAddPOLineItemPaneOpen={setAddPOLineItemPaneOpen}
                ></AddPOLineItemPane>
    
                {/* ===================================================== */}
                {/*  Edit PO Line Item Pane                               */}
                {/* ===================================================== */}
    
                {
                    editPOLineItemPaneOpen &&
                    (
                        <EditPOLineItemPane
                            task={task}
                            editPOLineItemPaneOpen={editPOLineItemPaneOpen}
                            setEditPOLineItemPaneOpen={setEditPOLineItemPaneOpen}
                            polineitemIndex={selectedPOLineItemIndex}
                        ></EditPOLineItemPane>
                    )
                }
    
            </div >
        )

    }

    
}
